import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Card, Row, Col, Form, Button, Badge, Pagination } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import { listarRateioTemplates, excluirRateioTemplate } from '~/Services/Service';
import ModalRateioTemplate from './ModalRateioTemplate';

const PAGE_SIZE = 10;

const RateioTemplate = () => {
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [loading, setLoading] = useState(false);
  const [ordenacao, setOrdenacao] = useState({ coluna: 'codigo', direcao: 'asc' });

  const [filtroCodigo, setFiltroCodigo] = useState('');
  const [filtroDescricao, setFiltroDescricao] = useState('');
  const debounceRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [templateSelecionado, setTemplateSelecionado] = useState(null);
  const [confirmacao, setConfirmacao] = useState({ show: false, item: null });

  const buscar = useCallback(async (pg, ord, codigo, descricao) => {
    setLoading(true);
    try {
      const params = {
        page: pg,
        pageSize: PAGE_SIZE,
        sortBy: ord.coluna,
        sortDir: ord.direcao,
      };
      if (codigo) params.codigo = codigo;
      if (descricao) params.descricao = descricao;
      const resp = await listarRateioTemplates(params);
      setItens(resp.itens || []);
      setTotal(resp.total || 0);
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao carregar templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    buscar(pagina, ordenacao, filtroCodigo, filtroDescricao);
  }, [pagina, ordenacao]); // eslint-disable-line react-hooks/exhaustive-deps

  const dispararBusca = (codigo, descricao) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPagina(1);
      buscar(1, ordenacao, codigo, descricao);
    }, 300);
  };

  const handleFiltroCodigo = (e) => {
    const val = e.target.value;
    setFiltroCodigo(val);
    dispararBusca(val, filtroDescricao);
  };

  const handleFiltroDescricao = (e) => {
    const val = e.target.value;
    setFiltroDescricao(val);
    dispararBusca(filtroCodigo, val);
  };

  const handleOrdenacaoChange = (novaOrdenacao) => {
    setOrdenacao(novaOrdenacao);
    setPagina(1);
  };

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const mudarPagina = (novaPagina) => {
    if (novaPagina < 1 || novaPagina > totalPaginas) return;
    setPagina(novaPagina);
  };

  const renderPaginacao = () => {
    if (totalPaginas <= 1) return null;
    const maxBotoes = 5;
    const itensNav = [];
    let inicio = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
    if (fim - inicio < maxBotoes - 1) {
      inicio = Math.max(1, fim - maxBotoes + 1);
    }

    itensNav.push(
      <Pagination.First key="first" onClick={() => mudarPagina(1)} disabled={pagina === 1} />
    );
    itensNav.push(
      <Pagination.Prev key="prev" onClick={() => mudarPagina(pagina - 1)} disabled={pagina === 1} />
    );
    if (inicio > 1) itensNav.push(<Pagination.Ellipsis key="e-start" disabled />);
    for (let n = inicio; n <= fim; n++) {
      const num = n;
      itensNav.push(
        <Pagination.Item key={num} active={num === pagina} onClick={() => mudarPagina(num)}>
          {num}
        </Pagination.Item>
      );
    }
    if (fim < totalPaginas) itensNav.push(<Pagination.Ellipsis key="e-end" disabled />);
    itensNav.push(
      <Pagination.Next key="next" onClick={() => mudarPagina(pagina + 1)} disabled={pagina === totalPaginas} />
    );
    itensNav.push(
      <Pagination.Last key="last" onClick={() => mudarPagina(totalPaginas)} disabled={pagina === totalPaginas} />
    );
    return <Pagination className="mb-0">{itensNav}</Pagination>;
  };

  const handleNovo = () => {
    setTemplateSelecionado(null);
    setShowModal(true);
  };

  const handleEditar = (item) => {
    setTemplateSelecionado(item);
    setShowModal(true);
  };

  const handleExcluir = (item) => {
    setConfirmacao({ show: true, item });
  };

  const handleConfirmarExclusao = async (confirmado) => {
    const item = confirmacao.item;
    setConfirmacao({ show: false, item: null });
    if (!confirmado) return;
    try {
      await excluirRateioTemplate(item.codigo);
      toast.success('Template excluído com sucesso.');
      const itensFinal = total - 1;
      const totalPag = Math.max(1, Math.ceil(itensFinal / PAGE_SIZE));
      const paginaFinal = Math.min(pagina, totalPag);
      if (paginaFinal !== pagina) {
        setPagina(paginaFinal);
      } else {
        buscar(pagina, ordenacao, filtroCodigo, filtroDescricao);
      }
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao excluir template.');
    }
  };

  const handleSalvar = () => {
    setShowModal(false);
    buscar(pagina, ordenacao, filtroCodigo, filtroDescricao);
  };

  const inicioRegistros = total === 0 ? 0 : (pagina - 1) * PAGE_SIZE + 1;
  const fimRegistros = Math.min(pagina * PAGE_SIZE, total);

  const colunas = [
    {
      key: 'codigo',
      title: 'Código',
      ordenacao: true,
      width: '90px',
      align: 'center',
    },
    {
      key: 'descricao',
      title: 'Descrição',
      ordenacao: true,
    },
    {
      key: 'qtd_departamentos',
      title: 'Departamentos',
      ordenacao: true,
      width: '130px',
      align: 'center',
    },
    {
      key: 'total_percentual',
      title: 'Total %',
      ordenacao: true,
      width: '130px',
      align: 'center',
      render: (val) => {
        const num = Number(val) || 0;
        return (
          <>
            {num.toFixed(2)}%{' '}
            {num < 100 && (
              <Badge variant="danger" style={{ fontSize: '0.7rem' }}>Incompleto</Badge>
            )}
          </>
        );
      },
    },
  ];

  const acoes = [
    {
      label: 'Editar',
      icon: <FaEdit />,
      onClick: handleEditar,
    },
    {
      label: 'Excluir',
      icon: <FaTrash />,
      type: 'danger',
      onClick: handleExcluir,
    },
  ];

  return (
    <Container fluid className="p-3">
      <Header title="Templates de Rateio" />

      <Card className="mb-3">
        <Card.Header>
          <strong>Filtros</strong>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={3}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">Código</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  value={filtroCodigo}
                  onChange={handleFiltroCodigo}
                  placeholder="Ex: 1"
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={9}>
              <Form.Group className="mb-2">
                <Form.Label className="small mb-1">Descrição</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={filtroDescricao}
                  onChange={handleFiltroDescricao}
                  placeholder="Buscar por descrição..."
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Templates de Rateio</strong>
          <Button size="sm" variant="primary" onClick={handleNovo}>
            <FaPlus className="mr-1" /> Novo Template
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="py-4"><Loading /></div>
          ) : (
            <TabelaDinamica
              colunas={colunas}
              dados={itens}
              action
              acoes={acoes}
              actionColumnWidth="150px"
              ordenacao={ordenacao}
              onOrdenacaoChange={handleOrdenacaoChange}
            />
          )}
        </Card.Body>
        {!loading && (
          <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap">
            <small className="text-muted">
              Mostrando {inicioRegistros} até {fimRegistros} de {total} registros
            </small>
            {renderPaginacao()}
          </Card.Footer>
        )}
      </Card>

      <ModalConfirmacao
        show={confirmacao.show}
        title="Confirmar Exclusão"
        message={
          confirmacao.item
            ? `Deseja excluir o template "${confirmacao.item.descricao || ''}" (Código ${confirmacao.item.codigo})?`
            : ''
        }
        onClose={handleConfirmarExclusao}
      />

      {showModal && (
        <ModalRateioTemplate
          show={showModal}
          onHide={() => setShowModal(false)}
          codigo={templateSelecionado ? templateSelecionado.codigo : null}
          onSalvar={handleSalvar}
        />
      )}
    </Container>
  );
};

export default RateioTemplate;
